// Persist visitor's most recent savings estimate (from any calculator)
// in localStorage so we can re-engage them in the footer geo card next time.
import { useEffect, useState } from "react";

const KEY = "carsavr_last_estimate_v1";
const TTL_DAYS = 30;
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;
const EVENT = "carsavr:estimate-updated";

// Categories of estimate so the footer copy can adapt.
// type: "insurance" | "refinance" | "combined"
//
// Iter-113 — Optional `inputs` field persists slider state from the
// homepage Live Estimate (premium / loan / rate) so SavingsCalculator
// can rehydrate the visitor's last position on return visits. Older
// payloads without `inputs` are still readable.
export function saveEstimate({ type, annual_savings, source, inputs }) {
  if (!Number.isFinite(annual_savings) || annual_savings <= 0) return;
  try {
    const payload = {
      type,
      annual_savings: Math.round(annual_savings),
      source: source || "unknown",
      ts: Date.now(),
    };
    if (inputs && typeof inputs === "object") {
      payload.inputs = {
        premium: Number(inputs.premium) || null,
        loan: Number(inputs.loan) || null,
        rate: Number(inputs.rate) || null,
      };
    }
    localStorage.setItem(KEY, JSON.stringify(payload));
    // Notify same-tab listeners (storage event only fires across tabs)
    window.dispatchEvent(new CustomEvent(EVENT, { detail: payload }));
  } catch {
    /* private mode / quota — ignore */
  }
}

export function clearEstimate() {
  try {
    localStorage.removeItem(KEY);
    window.dispatchEvent(new CustomEvent(EVENT, { detail: null }));
  } catch {
    /* ignore */
  }
}

function readEstimate() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || !data.ts || Date.now() - data.ts > TTL_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

// Iter-113 — Returns the persisted slider inputs from a prior save, or
// null if none. Used by the homepage Live Estimate to rehydrate the
// visitor's last position. Validates that each input is a finite number
// and within the slider's accepted range before returning.
export function loadEstimateInputs() {
  const data = readEstimate();
  if (!data || !data.inputs) return null;
  const { premium, loan, rate } = data.inputs;
  const inRange = (n, lo, hi) =>
    Number.isFinite(n) && n >= lo && n <= hi ? n : null;
  const p = inRange(premium, 50, 500);
  const l = inRange(loan, 3000, 70000);
  const r = inRange(rate, 3, 18);
  if (p == null || l == null || r == null) return null;
  return { premium: p, loan: l, rate: r };
}

export function useLastEstimate() {
  const [estimate, setEstimate] = useState(readEstimate);

  useEffect(() => {
    const refresh = () => setEstimate(readEstimate());
    // Cross-tab updates fire 'storage'; same-tab updates fire our custom event.
    window.addEventListener("storage", refresh);
    window.addEventListener(EVENT, refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener(EVENT, refresh);
    };
  }, []);

  return estimate; // null or { type, annual_savings, source, ts }
}
